// SQL/PGQ (property-graph) completion vocabulary + pure token/text helpers
// shared by the Oracle (Task 20) and future PG19 (Task 24) graph dispatch.
// Pure module — no Monaco / snapshot / React imports.
//
// Scope guard (Global Constraints, PRD §"path modes"): this vocabulary offers
// only the PGQ *scalar/row functions* the grammar surfaces at a GRAPH_TABLE
// COLUMNS position. It deliberately omits path modes / SHORTEST / unbounded
// quantifiers — those are pattern-quantifier syntax, never completion
// candidates.

import type { ClauseToken } from '@/features/sql-grammar/document/editorContextClause';
import { findEnclosingLparenIndex } from '@/features/sql-grammar/document/editorContextSlotShared';
import type { CompletionCandidate } from '@/core/engines/intellisenseTypes';

/**
 * The PGQ scalar/row functions surfaced at a `GRAPH_TABLE ( … COLUMNS ( ▮`
 * projection slot. `addedIn` mirrors the Oracle RU floor each function shipped
 * in (research memo §1, probe-pinned) and documents the gate; the actual
 * version decoration is driven by the `versionMetadata` catalog entries the
 * orchestrator's version-gating post-process consults on the emitted
 * `system-function` items.
 */
export const PGQ_FUNCTIONS: readonly { name: string; addedIn?: string }[] = [
  { name: 'VERTEX_ID' },
  { name: 'EDGE_ID' },
  { name: 'VERTEX_EQUAL' },
  { name: 'EDGE_EQUAL' },
  { name: 'BINDING_COUNT', addedIn: '23.6' },
  { name: 'MATCHNUM', addedIn: '23.8' },
  { name: 'ELEMENT_NUMBER', addedIn: '23.8' },
  { name: 'PATH_NAME', addedIn: '23.9' },
  { name: 'PROPERTY_EXISTS', addedIn: '23.9' },
];

/**
 * The PGQ scalar/row functions surfaced at PG's `GRAPH_TABLE ( … COLUMNS ( ▮`
 * projection slot. **Deliberately empty.**
 *
 * PROBE-PINNED against live PG19 (19beta1, db `dvdrental`, throwaway graph
 * `_probe.pg1`) on 2026-07-11: PostgreSQL 19's SQL/PGQ implementation ships
 * NONE of the Oracle-style graph-element reference functions — `pg_proc`
 * carries no VERTEX_ID / EDGE_ID / VERTEX_EQUAL / EDGE_EQUAL / BINDING_COUNT /
 * MATCHNUM / ELEMENT_NUMBER / PATH_NAME / PROPERTY_EXISTS (each
 * `SELECT g.x FROM GRAPH_TABLE(_probe.pg1 MATCH (a) COLUMNS (VERTEX_ID(a) AS x)) g`
 * probe returned `column "a" does not exist`, and `MATCHNUM()` returned
 * `function matchnum() does not exist`; a `pg_proc` scan for those names
 * found only `pg_get_propgraphdef`). PG19's COLUMNS clause accepts only
 * property projections (`a.name`, `e.since`, `a.id + 1`); a bare element
 * variable (`a AS x`) is itself rejected. So PG's projection slot offers the
 * element variables (for `var.prop` navigation) + the `AS` keyword, and no
 * PGQ functions. Kept as a const (rather than inlining `[]`) so a future PG
 * release that adds functions extends this one list, byte-identically to how
 * Oracle consumes `PGQ_FUNCTIONS`.
 */
export const PG_PGQ_FUNCTIONS: readonly { name: string; addedIn?: string }[] = [];

/** Uppercased text of a keyword/identifier token, or null for other kinds. */
function tokenUpper(t: ClauseToken | undefined): string | null {
  if (!t) return null;
  if (t.kind !== 'identifier' && t.kind !== 'keyword') return null;
  return t.text.toUpperCase();
}

export type GraphTableInfo = {
  /** The graph name typed right after `GRAPH_TABLE (`, or null when absent
   *  (degenerate `GRAPH_TABLE( ▮` / `GRAPH_TABLE( MATCH …`). Raw token text —
   *  the caller folds for case-insensitive lookup. */
  readonly graphName: string | null;
  /** Source offset just past the `GRAPH_TABLE (` — start of the graph body
   *  (used to bound the element-variable scan + node/edge pattern scan). */
  readonly bodyStart: number;
};

/**
 * Locate the innermost enclosing (unclosed) `GRAPH_TABLE (` in a
 * cursor-truncated token stream and read the graph name that follows it.
 * Mirrors `editorContextSlot.graph.ts`'s `findGraphTableLparenIndex` walk
 * (balanced-paren stack, innermost-out) so the resolver keys on the SAME
 * enclosing paren the slot classifier does. Returns null when the cursor is
 * not inside a `GRAPH_TABLE (`.
 */
export function findGraphTableInfo(tokens: readonly ClauseToken[]): GraphTableInfo | null {
  let depth = 0;
  for (let i = tokens.length - 1; i >= 0; i -= 1) {
    const t = tokens[i]!;
    if (t.kind === 'rparen') {
      depth += 1;
    } else if (t.kind === 'lparen') {
      if (depth === 0) {
        if (tokenUpper(tokens[i - 1]) === 'GRAPH_TABLE') {
          const nameTok = tokens[i + 1];
          const nameUpper = tokenUpper(nameTok);
          const graphName =
            nameTok &&
            (nameTok.kind === 'identifier' || nameTok.kind === 'keyword') &&
            nameUpper !== 'MATCH'
              ? nameTok.text
              : null;
          return { graphName, bodyStart: t.end };
        }
        // An inner node/edge/COLUMNS paren — keep walking outward.
      } else {
        depth -= 1;
      }
    }
  }
  return null;
}

/**
 * The graph-body structural keyword the cursor expects at a DETACHED top-level
 * `GRAPH_TABLE ( … )` position that owns no name/label/property/columns
 * vocabulary slot — `classifyGraphSlot` returns null there by design (T18), so
 * the resolvers consult this to surface the one legal keyword:
 *
 *   'match'   — `GRAPH_TABLE(g ▮` : a complete graph name, cursor detached, no
 *               MATCH yet. MATCH opens the pattern.
 *   'columns' — `GRAPH_TABLE(g MATCH (a) ▮` : a complete top-level MATCH pattern
 *               (cursor at graph-body depth, previous token closes a node/edge
 *               pattern), no COLUMNS yet. COLUMNS opens the projection.
 *   null      — the cursor is attached (still typing), inside a nested
 *               paren/bracket, or the structural position is already occupied
 *               (e.g. `… MATCH ▮` expecting a pattern `(`).
 *
 * Pure token walk (mirrors `findGraphTableInfo`'s balanced-paren stack) — no
 * snapshot / Monaco. `cursorOffset` gates the attached-tail convention.
 */
export function graphStructuralKeyword(
  tokens: readonly ClauseToken[],
  cursorOffset: number,
): 'match' | 'columns' | null {
  // Locate the innermost enclosing (unclosed) `GRAPH_TABLE (` and its index.
  let depth = 0;
  let graphLparen = -1;
  for (let i = tokens.length - 1; i >= 0; i -= 1) {
    const t = tokens[i]!;
    if (t.kind === 'rparen') {
      depth += 1;
    } else if (t.kind === 'lparen') {
      if (depth === 0) {
        if (tokenUpper(tokens[i - 1]) === 'GRAPH_TABLE') { graphLparen = i; break; }
      } else {
        depth -= 1;
      }
    }
  }
  if (graphLparen < 0) return null;

  // The cursor must sit at the TOP level of the graph body: the innermost
  // enclosing unclosed `(` IS the graph paren (never inside a node/edge/COLUMNS
  // paren, which own their own slots).
  if (findEnclosingLparenIndex(tokens) !== graphLparen) return null;

  const body = tokens.slice(graphLparen + 1);
  if (body.length === 0) return null; // `GRAPH_TABLE( ▮` — the graph-name slot owns this.

  // Only a DETACHED position (a whitespace gap after the last body token) is a
  // structural gap — an attached tail means the user is mid-token.
  const last = body[body.length - 1]!;
  if (last.end >= cursorOffset) return null;

  let hasMatch = false;
  let hasColumns = false;
  for (const t of body) {
    const u = tokenUpper(t);
    if (u === 'MATCH') hasMatch = true;
    else if (u === 'COLUMNS') hasColumns = true;
  }

  // Before MATCH: a complete graph name precedes the cursor → MATCH is next.
  if (!hasMatch) {
    return last.kind === 'identifier' || last.kind === 'keyword' ? 'match' : null;
  }
  // After a complete top-level MATCH pattern (last token closes a pattern) and
  // before COLUMNS → COLUMNS is next. (`MATCH ▮` / `(a), ▮` are incomplete →
  // null, so the resolver stays silent there.)
  if (!hasColumns && last.kind === 'rparen') return 'columns';
  return null;
}

/**
 * The MATCH/COLUMNS structural-keyword candidate surfaced at a detached
 * top-level GRAPH_TABLE position (see `graphStructuralKeyword`). Shared by both
 * the Oracle (Task 20) and PG (Task 24) resolvers so the keyword shape stays
 * identical across engines.
 */
export function graphStructuralKeywordCandidate(kw: 'match' | 'columns'): CompletionCandidate {
  const label = kw === 'match' ? 'MATCH' : 'COLUMNS';
  return {
    label,
    insertText: label,
    isSnippet: false,
    kind: 'keyword',
    tier: 7,
    detail: 'SQL/PGQ keyword',
  };
}

export type GraphPatternContext = 'vertex' | 'edge' | 'unknown';

/**
 * Whether the cursor's label slot sits inside a node pattern `( … )` (vertex)
 * or an edge pattern `-[ … ]-` (edge). GPML brackets are transparent to the
 * SQL tokenizer, so this is a raw-text bracket scan: walk back from the cursor
 * to the graph body start and return the type of the nearest unclosed bracket
 * — `[` → edge, `(` → vertex. `unknown` when neither encloses the cursor.
 */
export function graphPatternContext(
  text: string,
  bodyStart: number,
  cursor: number,
): GraphPatternContext {
  let paren = 0;
  let brack = 0;
  for (let i = cursor - 1; i >= bodyStart; i -= 1) {
    const ch = text[i];
    if (ch === ')') paren += 1;
    else if (ch === '(') {
      if (paren === 0) return 'vertex';
      paren -= 1;
    } else if (ch === ']') brack += 1;
    else if (ch === '[') {
      if (brack === 0) return 'edge';
      brack -= 1;
    }
  }
  return 'unknown';
}

/**
 * Case-insensitive Oracle-fold match of a typed graph/label/variable name
 * against a stored name. Both sides are unquoted then upper-compared — Oracle
 * folds unquoted identifiers to UPPERCASE in the data dictionary, so a typed
 * `eg` matches a stored `EG`. Double-quoted typed forms keep their case.
 */
export function graphIdentEquals(typed: string | null | undefined, stored: string): boolean {
  if (!typed) return false;
  const foldTyped =
    typed.length >= 2 && typed[0] === '"' && typed[typed.length - 1] === '"'
      ? typed.slice(1, -1).replace(/""/g, '"')
      : typed.toUpperCase();
  const foldStored =
    stored.length >= 2 && stored[0] === '"' && stored[stored.length - 1] === '"'
      ? stored.slice(1, -1).replace(/""/g, '"')
      : stored.toUpperCase();
  return foldTyped === foldStored;
}
